import * as React from "react";
import { Button } from "@/components/ui/button";
import moment from "moment";
import { useMemo } from "react";
import CountdownTimer from "@/containers/RecordVideo/components/CountdownTimer";

interface Props {
  recordingState: "toStart" | "Review" | "Recording" | "Done";
  disabled: boolean;
  doneDisabled: boolean;
  startHandler: () => void;
  stopHandler: () => void;
  startOverHandler: () => void;
  doneHandler: () => void;
  secondsRecorded: number;
}

const dataElmId = "recordActionSection";

const RecordActionSectionFC: React.FC<Props> = ({
  recordingState,
  disabled,
  doneDisabled,
  startHandler,
  stopHandler,
  startOverHandler,
  doneHandler,
  secondsRecorded,
}) => {
  const [showCountdown, setShowCountdown] = React.useState<boolean>(false);

  const isRecording = useMemo(() => {
    return recordingState === "Recording";
  }, [recordingState]);

  const recordingStatusLabel = useMemo(() => {
    return isRecording ? "Recording" : "Stopped";
  }, [isRecording]);

  const recordAction = (): void => {
    switch (recordingState) {
      case "toStart":
        setShowCountdown(true);
        break;
      case "Review":
      case "Done":
        break;
      case "Recording":
        stopHandler();
        break;
    }
  };

  const startOverAction = (): void => {
    startOverHandler();
  };

  const doneRecordingAction = async () => {
    doneHandler();
  };

  const actionButtonLabel = useMemo(() => {
    switch (recordingState) {
      case "toStart":
        return "Start Recording";
      case "Review":
      case "Done":
        return "Re-Record";
      case "Recording":
        return "Stop Recording";
    }
  }, [recordingState]);

  return (
    <>
      {showCountdown && (
        <CountdownTimer
          onFinish={() => {
            setShowCountdown(false);
            startHandler();
          }}
        />
      )}

      {/* Center status and recording actions (50% width in parent flex row) */}
      <div className="w-1/2 flex items-center justify-center gap-4">
        <span className="text-sm font-semibold text-gray-700 select-none">
          {recordingStatusLabel} - {moment.utc(secondsRecorded * 1000).format("mm:ss")}
        </span>
        {recordingState === "toStart" || recordingState === "Recording" ? (
          <Button
            disabled={disabled}
            variant={isRecording ? "destructive" : "default"}
            data-elm-id={`${dataElmId}ActionBtn`}
            onClick={recordAction}
            className="font-semibold shadow-2xs"
          >
            {actionButtonLabel}
          </Button>
        ) : (
          <Button
            variant="secondary"
            data-elm-id={`${dataElmId}StartOverBtn`}
            className="ml-2.5 font-semibold shadow-3xs"
            onClick={startOverAction}
            disabled={isRecording}
          >
            Start Over
          </Button>
        )}
      </div>

      {/* Right side done actions (25% width in parent flex row) */}
      <div className="w-1/4 text-right">
        {(recordingState === "Review" || recordingState === "Done") && (
          <Button
            className="text-sm font-bold shadow-2xs"
            data-elm-id={`${dataElmId}DoneBtn`}
            disabled={doneDisabled}
            onClick={doneRecordingAction}
          >
            Done Recording!
          </Button>
        )}
      </div>
    </>
  );
};

export const RecordActionSection = RecordActionSectionFC;
